import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Chip,
  Grid,
  Stack,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import {
  Megaphone,
  Plus,
  Trash2,
  Edit2,
  Eye,
  Calendar,
  Pin,
  X,
  Upload,
  Paperclip,
  FileText,
  Download,
} from 'lucide-react';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { TiptapEditor } from '../components/TiptapEditor';

const resolveFileUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `/api/v1${url}`;
  if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
  return url;
};

interface AttachmentItem {
  name: string;
  url: string;
  size?: number;
}

const AnnouncementManager: React.FC = () => {
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<any | null>(null);

  const [formData, setFormData] = useState<{
    title: string;
    content: string;
    banner_url: string;
    target_role: string;
    is_pinned: boolean;
    show_slider_title: boolean;
    attachments: AttachmentItem[];
  }>({
    title: '',
    content: '',
    banner_url: '',
    target_role: 'ALL',
    is_pinned: false,
    show_slider_title: true,
    attachments: [],
  });

  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrStaff = ['ADMIN', 'STAFF'].includes(user.role);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements');
      setAnnouncements(response.data || []);
    } catch (err) {
      console.error('Failed to fetch announcements', err);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setFormData({
      title: '',
      content: '',
      banner_url: '',
      target_role: 'ALL',
      is_pinned: false,
      show_slider_title: true,
      attachments: [],
    });
    setBannerFile(null);
    setBannerPreview('');
    setOpenDialog(true);
  };

  const handleOpenEdit = (ann: any) => {
    setEditingId(ann.id);

    let parsedAttachments: AttachmentItem[] = [];
    try {
      if (ann.attachments_json) {
        parsedAttachments = typeof ann.attachments_json === 'string'
          ? JSON.parse(ann.attachments_json)
          : ann.attachments_json;
      }
    } catch (e) {
      parsedAttachments = [];
    }

    setFormData({
      title: ann.title || '',
      content: ann.content || '',
      banner_url: ann.banner_url || '',
      target_role: ann.target_role || 'ALL',
      is_pinned: Boolean(ann.is_pinned),
      show_slider_title: ann.show_slider_title !== 0 && ann.show_slider_title !== false && ann.show_slider_title !== '0',
      attachments: Array.isArray(parsedAttachments) ? parsedAttachments : [],
    });
    setBannerFile(null);
    setBannerPreview(ann.banner_url ? resolveFileUrl(ann.banner_url) : '');
    setOpenDialog(true);
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveBanner = () => {
    setBannerFile(null);
    setBannerPreview('');
    setFormData((prev) => ({ ...prev, banner_url: '' }));
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAttachment(true);
      const data = new FormData();
      data.append('file', file);
      const res = await api.post('/announcements/upload-media', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.url) {
        setFormData((prev) => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            { name: file.name, url: res.data.url, size: file.size },
          ],
        }));
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์เอกสารแนบ');
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('กรุณากรอกหัวข้อประกาศ');
      return;
    }

    try {
      setSaving(true);
      const data = new FormData();
      data.append('title', formData.title);
      data.append('content', formData.content);
      data.append('target_role', formData.target_role);
      data.append('is_pinned', formData.is_pinned ? '1' : '0');
      data.append('show_slider_title', formData.show_slider_title ? '1' : '0');
      data.append('attachments_json', JSON.stringify(formData.attachments));

      if (bannerFile) {
        data.append('banner', bannerFile);
      } else if (formData.banner_url) {
        data.append('banner_url', formData.banner_url);
      } else {
        data.append('banner_url', '');
      }

      if (editingId) {
        await api.put(`/announcements/${editingId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/announcements', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setOpenDialog(false);
      fetchAnnouncements();
    } catch (err: any) {
      alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกประกาศ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบประกาศนี้?')) {
      try {
        await api.delete(`/announcements/${id}`);
        fetchAnnouncements();
      } catch (err: any) {
        alert(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบ');
      }
    }
  };

  const getTargetRoleBadge = (role: string) => {
    switch (role) {
      case 'STUDENT':
        return <Chip label="เฉพาะนักศึกษา" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 600 }} />;
      case 'SUPERVISOR':
        return <Chip label="เฉพาะอาจารย์นิเทศก์" size="small" sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 600 }} />;
      case 'CO_TEACHER':
        return <Chip label="เฉพาะครูพี่เลี้ยง" size="small" sx={{ bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 600 }} />;
      default:
        return <Chip label="ประชาสัมพันธ์ทั่วไป" size="small" sx={{ bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600 }} />;
    }
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Megaphone size={28} color="#1E40AF" />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>
              จัดการข่าวประชาสัมพันธ์และประกาศ
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            สร้างและเผยแพร่ข่าวสาร กำหนดการ และประกาศสำคัญไปยังนักศึกษา อาจารย์นิเทศก์ และครูพี่เลี้ยง
          </Typography>
        </Box>

        {isAdminOrStaff && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus size={18} />}
            onClick={handleOpenCreate}
            sx={{ borderRadius: 2.5, px: 2.5, fontWeight: 700 }}
          >
            สร้างประกาศใหม่
          </Button>
        )}
      </Box>

      {/* Announcements List */}
      <Stack spacing={2.5}>
        {announcements.map((ann) => {
          let atts: AttachmentItem[] = [];
          try {
            if (ann.attachments_json) {
              atts = typeof ann.attachments_json === 'string' ? JSON.parse(ann.attachments_json) : ann.attachments_json;
            }
          } catch (e) {
            atts = [];
          }

          return (
            <GlassCard
              key={ann.id}
              highlightColor={ann.is_pinned ? '#D97706' : '#2563EB'}
              sx={{ p: { xs: 2.5, sm: 3 } }}
            >
              <Grid container spacing={3} sx={{ alignItems: 'center' }}>
                {/* Optional Banner Thumbnail */}
                {ann.banner_url && (
                  <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 140,
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: '#F1F5F9',
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={resolveFileUrl(ann.banner_url)}
                        alt={ann.title}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </Grid>
                )}

                {/* Text Info */}
                <Grid size={{ xs: 12, sm: ann.banner_url ? 8 : 12, md: ann.banner_url ? 9 : 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {ann.is_pinned ? (
                      <Chip
                        icon={<Pin size={13} />}
                        label="ปักหมุดสำคัญ"
                        size="small"
                        sx={{ bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }}
                      />
                    ) : null}
                    {getTargetRoleBadge(ann.target_role)}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: '0.82rem' }}>
                      <Calendar size={14} />
                      <span>
                        {new Date(ann.created_at).toLocaleDateString('th-TH', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B', fontSize: '0.82rem' }}>
                      <Eye size={14} />
                      <span>อ่าน {ann.views_count || 0} ครั้ง</span>
                    </Box>

                    {/* Show Slider Title indicator */}
                    {ann.banner_url && (ann.show_slider_title === 0 || ann.show_slider_title === false) ? (
                      <Chip
                        label="ซ่อนข้อความบนภาพสไลด์"
                        size="small"
                        variant="outlined"
                        sx={{ color: '#64748B', borderColor: '#CBD5E1', fontSize: '0.75rem' }}
                      />
                    ) : null}

                    {/* Attachments indicator */}
                    {Array.isArray(atts) && atts.length > 0 ? (
                      <Chip
                        icon={<Paperclip size={12} />}
                        label={`เอกสารแนบ ${atts.length} ไฟล์`}
                        size="small"
                        sx={{ bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 600 }}
                      />
                    ) : null}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E3A8A', mb: 1, lineHeight: 1.3 }}>
                    {ann.title}
                  </Typography>

                  {/* Content snippet */}
                  <Box
                    sx={{
                      color: '#475569',
                      fontSize: '0.92rem',
                      lineHeight: 1.6,
                      maxHeight: 60,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                      '& p': { m: 0 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: (ann.content || '')
                        .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                        .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
                    }}
                  />

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      เผยแพร่โดย: <strong>{ann.author_name}</strong>
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Eye size={15} />}
                        onClick={() => setPreviewItem(ann)}
                        sx={{ borderRadius: 2 }}
                      >
                        อ่านเนื้อหาเต็ม
                      </Button>

                      {isAdminOrStaff && (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEdit(ann)}
                            title="แก้ไขประกาศ"
                            sx={{ bgcolor: '#EFF6FF' }}
                          >
                            <Edit2 size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(ann.id)}
                            title="ลบประกาศ"
                            sx={{ bgcolor: '#FEF2F2' }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </GlassCard>
          );
        })}

        {announcements.length === 0 && (
          <GlassCard sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              ยังไม่มีรายการข่าวประชาสัมพันธ์หรือประกาศ
            </Typography>
          </GlassCard>
        )}
      </Stack>

      {/* CREATE / EDIT ANNOUNCEMENT DIALOG WITH TIPTAP & BANNER */}
      <Dialog
        open={openDialog}
        onClose={() => !saving && setOpenDialog(false)}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { borderRadius: 3, p: 1 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E3A8A', borderBottom: '1px solid #E2E8F0', pb: 2 }}>
          {editingId ? 'แก้ไขประกาศข่าวสาร' : 'สร้างประกาศข่าวสารใหม่'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Title */}
            <TextField
              fullWidth
              label="หัวข้อประกาศ *"
              placeholder="ระบุหัวข้อข่าวสารหรือประกาศ..."
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            {/* Target Role & Pinning */}
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="กลุ่มเป้าหมาย"
                  value={formData.target_role}
                  onChange={(e) => setFormData({ ...formData, target_role: e.target.value })}
                >
                  <MenuItem value="ALL">ทุกคน (ประชาสัมพันธ์สาธารณะ)</MenuItem>
                  <MenuItem value="STUDENT">เฉพาะนักศึกษาฝึกสอน</MenuItem>
                  <MenuItem value="SUPERVISOR">เฉพาะอาจารย์นิเทศก์</MenuItem>
                  <MenuItem value="CO_TEACHER">เฉพาะครูพี่เลี้ยง</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_pinned}
                      onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                      color="warning"
                    />
                  }
                  label="ปักหมุดข่าวสำคัญ (แสดงลำดับแรก)"
                />
              </Grid>
            </Grid>

            {/* Banner Image Upload & Preview */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                ภาพแบนเนอร์ประชาสัมพันธ์ (สำหรับแสดงใน Image Slider หน้าแรกและส่วนหัวข่าว)
              </Typography>

              {bannerPreview ? (
                <Box sx={{ position: 'relative', width: '100%', height: 200, borderRadius: 2.5, overflow: 'hidden', border: '1px solid #CBD5E1' }}>
                  <CardMedia
                    component="img"
                    image={bannerPreview}
                    alt="Banner Preview"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveBanner}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: 'rgba(0,0,0,0.65)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                    }}
                  >
                    <X size={18} />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<Upload size={18} />}
                  sx={{
                    py: 2.5,
                    borderStyle: 'dashed',
                    borderRadius: 2.5,
                    color: '#64748B',
                    borderColor: '#CBD5E1',
                    bgcolor: '#F8FAFC',
                    '&:hover': { bgcolor: '#EFF6FF', borderColor: '#1E40AF' },
                  }}
                >
                  คลิกเพื่อเลือกไฟล์ภาพแบนเนอร์ (JPG, PNG, WebP)
                  <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
                </Button>
              )}
            </Box>

            {/* Show Slider Title Switch */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_slider_title}
                    onChange={(e) => setFormData({ ...formData, show_slider_title: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                      แสดงกล่องข้อความชื่อประกาศบนภาพสไลด์หน้าแรก (Hero Carousel)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      * หากภาพแบนเนอร์มีข้อความกราฟิกอยู่แล้ว สามารถปิดตัวเลือกนี้เพื่อไม่ให้กล่องข้อความแสดงทับซ้อนบนรูปภาพ โดยผู้ใช้ยังสามารถคลิกที่รูปภาพสไลด์เพื่อเปิดอ่านข่าวได้ตามปกติ
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Rich Text Editor for Content using Tiptap */}
            <Box>
              <TiptapEditor
                label="เนื้อหาประกาศและรายละเอียด (Rich Text) *"
                value={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                placeholder="เขียนรายละเอียดข่าวสาร ประกาศ ข้อความ หรือกำหนดการต่างๆ สามารถแทรกรูปภาพ แนบลิงก์ หรือจัดรูปแบบข้อความได้ที่นี่..."
                minHeight={220}
                uploadEndpoint="/announcements/upload-media"
                enableMedia={true}
              />
            </Box>

            {/* Attachments Section */}
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Paperclip size={18} color="#1E40AF" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A' }}>
                    ไฟล์เอกสารแนบประกาศ (ดาวน์โหลดได้ในหน้ารายละเอียดข่าว)
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={uploadingAttachment ? <CircularProgress size={14} /> : <Plus size={14} />}
                  disabled={uploadingAttachment}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  {uploadingAttachment ? 'กำลังอัปโหลด...' : 'เพิ่มไฟล์เอกสารแนบ'}
                  <input
                    type="file"
                    hidden
                    ref={attachmentInputRef}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                    onChange={handleAttachmentUpload}
                  />
                </Button>
              </Box>

              {formData.attachments.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>
                  ยังไม่มีไฟล์เอกสารแนบ (สามารถแนบไฟล์ PDF, Word, Excel หรือหนังสือราชการเพิ่มเติมได้ที่นี่)
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {formData.attachments.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#FFFFFF',
                        p: 1.2,
                        px: 1.8,
                        borderRadius: 2,
                        border: '1px solid #E2E8F0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
                        <FileText size={18} color="#059669" />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }} noWrap>
                          {file.name}
                        </Typography>
                        {file.size ? (
                          <Typography variant="caption" color="text.secondary">
                            ({file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`})
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton size="small" onClick={() => handleRemoveAttachment(idx)} sx={{ color: '#EF4444' }}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setOpenDialog(false)} disabled={saving}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {saving ? 'กำลังบันทึก...' : editingId ? 'บันทึกการแก้ไข' : 'เผยแพร่ประกาศ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FULL ARTICLE PREVIEW MODAL */}
      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { borderRadius: 3, overflow: 'hidden' },
          },
        }}
      >
        {previewItem && (() => {
          let previewAtts: AttachmentItem[] = [];
          try {
            if (previewItem.attachments_json) {
              previewAtts = typeof previewItem.attachments_json === 'string'
                ? JSON.parse(previewItem.attachments_json)
                : previewItem.attachments_json;
            }
          } catch (e) {
            previewAtts = [];
          }

          return (
            <>
              {previewItem.banner_url && (
                <Box sx={{ width: '100%', height: { xs: 180, sm: 280 }, bgcolor: '#F1F5F9' }}>
                  <CardMedia
                    component="img"
                    image={resolveFileUrl(previewItem.banner_url)}
                    alt={previewItem.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              )}

              <DialogTitle sx={{ pt: 3, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {previewItem.is_pinned ? (
                    <Chip icon={<Pin size={13} />} label="ข่าวสำคัญ" size="small" sx={{ bgcolor: '#FEF3C7', color: '#B45309' }} />
                  ) : null}
                  {getTargetRoleBadge(previewItem.target_role)}
                  <Typography variant="caption" color="text.secondary">
                    {new Date(previewItem.created_at).toLocaleDateString('th-TH', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E3A8A', lineHeight: 1.35 }}>
                  {previewItem.title}
                </Typography>
              </DialogTitle>

              <DialogContent dividers sx={{ py: 3 }}>
                <Box
                  sx={{
                    fontFamily: "'Sarabun', sans-serif",
                    fontSize: '1rem',
                    lineHeight: 1.8,
                    color: '#1E293B',
                    '& h2': { color: '#1E3A8A', mt: 2, mb: 1, fontFamily: "'Prompt', sans-serif" },
                    '& h3': { color: '#1E40AF', mt: 1.5, mb: 0.8, fontFamily: "'Prompt', sans-serif" },
                    '& img': { maxWidth: '100%', borderRadius: 8, my: 1.5 },
                    '& blockquote': {
                      borderLeft: '4px solid #3B82F6',
                      bgcolor: '#F8FAFC',
                      p: 1.5,
                      my: 1.5,
                      borderRadius: '0 8px 8px 0',
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: (previewItem.content || '')
                      .replace(/src="\/uploads\//g, 'src="/api/v1/uploads/')
                      .replace(/href="\/uploads\//g, 'href="/api/v1/uploads/'),
                  }}
                />

                {/* Attachments inside modal */}
                {Array.isArray(previewAtts) && previewAtts.length > 0 && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#F8FAFC', borderRadius: 2.5, border: '1px solid #E2E8F0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A8A', mb: 1 }}>
                      เอกสารแนบที่เกี่ยวข้อง ({previewAtts.length} ไฟล์):
                    </Typography>
                    <Stack spacing={1}>
                      {previewAtts.map((att, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: '#FFFFFF',
                            p: 1.2,
                            borderRadius: 2,
                            border: '1px solid #E2E8F0',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileText size={16} color="#059669" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{att.name}</Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            href={resolveFileUrl(att.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Download size={13} />}
                          >
                            ดาวน์โหลด
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                  เผยแพร่โดย: {previewItem.author_name}
                </Typography>
                <Button onClick={() => setPreviewItem(null)} variant="contained" sx={{ borderRadius: 2 }}>
                  ปิดหน้าต่าง
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default AnnouncementManager;
